import {
    checkClaimsWeb,
    EventrDate,
    LiveLocation,
    spLogTags,
    V2,
} from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    getContrastYIQ,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack, Tab, Tabs } from '@mui/material'
import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useScroll,
} from 'framer-motion'
import { useMemo, useRef, useState } from 'react'
import FallbackHeaderImageSource from '../../assets/illustrations/defaultSplash.png'

// PROVIDERS:

import { useRocketChatContext } from '../../feat/rocketchat/rocketChatProvider'

// SUB-COMPONENTS:

import { LngLat } from 'maplibre-gl'
import { useSwipeable } from 'react-swipeable'
import { useShallow } from 'zustand/react/shallow'
import { ProfileDetails } from '../../feat/account/profilePage/profileDetails'
import { ToolbarItem, ToolbarItemProps } from '../../feat/layout/TopToolbar'
import { Paths } from '../../routes'
import { bs, useBoundStore } from '../../state/bound.store'
import { homeTabSignal } from '../../state/itineraryState/itinerarySignals'
import { useLiveLocationStore } from '../../state/liveLocationState/liveLocation.store'
import { loadingSignal } from '../../state/overlayState/overlaySignals'
import { useEphemeralStore } from '../../state/useEphemeralStore'
import { windowSizes } from '../../state/windowState/windowSignals'
import { RendererSplitter } from '../../v2/rendererSplitter'
import SlideCardRow from '../components/carousel/slide/SlideCardRow'
import { SlideImage } from '../components/carousel/slide/SlideImage'
import { AddressLabel } from '../components/map/components/mapDrawer'
import { MemberProfilePic } from '../team/MemberProfilePic'
import i18n from '../../i18n/i18next'
import { LngLatLabelledAppUser } from '../components/map/interfaces/locationLabelledObject'

// An interface displaying the itinerary and profile of a user. The user
// information is pulled from the user ID stored in the override property of the
// User Provider.
export function TeamMember() {
    // PROVIDERS:

    const {
        orgConfig,
        override,
        wpUser,
        selectedItinerary,
        getUserFromID,
        getCurrentUser,
        getEventInfoV2,
        filterEntitiesUserSpecific,
    } = useBoundStore(
        useShallow((s) => {
            return {
                orgConfig: s.orgConfig,
                override: s.override,
                wpUser: s.wpUser,
                selectedItinerary: s.selectedItinerary,
                getUserFromID: s.getUserFromID,
                getCurrentUser: s.getCurrentUser,
                getEventInfoV2: s.getEventInfoV2,
                filterEntitiesUserSpecific: s.filterEntitiesUserSpecific,
            }
        })
    )
    const { createDirectChannel, openChat, chatView } = useRocketChatContext()

    // STATE:
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)
    const [headerCollapsed, setHeaderCollapsed] = useState<boolean>(false)

    // CONSTANTS:

    const currentUser = getCurrentUser()

    const tabs = ['Itinerary']

    if (checkClaimsWeb(['/'], ['userPrivateInfoViewer'])) {
        tabs.push('Profile')
    }

    const selectedUser = getUserFromID(override || 1)

    const backButton: ToolbarItemProps = {
        id: 'backButtonTeamMember',
        icon: 'Chevron_Left',
        action: closeOverlay,
    }

    const chatButton: ToolbarItemProps = {
        id: 'chatButton',
        icon: 'Chat',
        action: () => {
            loadingSignal.value = true
            createDirectChannel({
                user_id: currentUser?.id ?? -1,
                user_id2: wpUser?.id ?? -1,
            })
                .then((chat) => {
                    if (chat.status === 'success') {
                        closeOverlay()
                        chatView.value = 'default'
                        openChat(chat.data.channel_id, 'd')
                    } else {
                        spLogTags('Error creating direct chat', [
                            'ERROR',
                            'rocketchat',
                        ])
                    }
                })
                .finally(() => {
                    loadingSignal.value = false
                })
        },
    }

    // SCROLL-RELATED PROPERTIES

    const containerRef = useRef(null)

    const { scrollY } = useScroll({
        container: containerRef,
    })

    useMotionValueEvent(scrollY, 'change', (latest) => {
        setHeaderCollapsed(!(latest <= 0))
    })

    // FUNCTIONS:

    function closeOverlay() {
        drawerStore().closeDrawer()
        bs().setOverride(null)
    }

    function dataToDisplay(data: V2[]) {
        let temp: any[] = []

        data.forEach((v2) => {
            if (v2.rendererOptions.settings.userList) {
                const filteredData: any[] = filterEntitiesUserSpecific(
                    'teamMember',
                    v2,
                    v2.rendererOptions.settings.userList
                )
                temp = temp.concat(filteredData)
            }
        })

        if (temp.length === 0) {
            return false
        } else {
            return true
        }
    }

    const dismissHandlers = useSwipeable({
        onSwipedDown: () => {
            closeOverlay()
        },
    })

    const liveLocations = useLiveLocationStore(
        useShallow((state) => state.liveLocations)
    )

    const userLocation: LiveLocation | undefined = useMemo(() => {
        const user = getUserFromID(override || 1)
        if (user) {
            return liveLocations.get(String(user?.id)) // Convert 'id' to string
        }
        return undefined
    }, [liveLocations])

    const { selectLngLatLabelledTeamMember } = useEphemeralStore(
        useShallow((s) => {
            return {
                selectLngLatLabelledTeamMember:
                    s.selectLngLatLabelledTeamMember,
            }
        })
    )

    return (
        <Box
            height={'100dvh'}
            sx={{
                display: 'grid',
                alignContent: 'start',
                backgroundColor: ColoursObj.backgrounds.primary,
            }}
        >
            <Box>
                <SlideImage
                    src={
                        getEventInfoV2()?.data.cover_image ??
                        FallbackHeaderImageSource
                    }
                    style={{
                        paddingTop: windowSizes.value.insets?.insets.top,
                        position: 'sticky',
                        width: '100vw',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        padding="8px 8px 0px "
                    >
                        <ToolbarItem props={backButton} />

                        <Stack alignItems="center">
                            <MemberProfilePic
                                user={selectedUser}
                                sideLength={headerCollapsed ? 40 : 128}
                            />

                            {selectedUser &&
                                selectedUser.firstName &&
                                selectedUser.lastName && (
                                    <Typography
                                        paddingY="4px"
                                        variant="bodyBold"
                                        color={ColoursObj.shades.shade5}
                                        style={{
                                            transition: '0.5s',
                                            transform: `scale(${
                                                headerCollapsed ? 0.8 : 1
                                            })`,
                                        }}
                                    >
                                        {[
                                            selectedUser.firstName,
                                            selectedUser.lastName,
                                        ].join(' ')}
                                    </Typography>
                                )}

                            <AnimatePresence>
                                {!headerCollapsed && (
                                    <motion.div
                                        initial={{
                                            height: 0,
                                            opacity: 0,
                                            scale: 0.8,
                                        }}
                                        animate={{
                                            height: 'auto',
                                            opacity: 1,
                                            scale: 1.0,
                                        }}
                                        exit={{
                                            height: 0,
                                            opacity: 0,
                                            scale: 0.8,
                                        }}
                                        transition={{
                                            type: 'spring',
                                            duration: 0.5,
                                            bounce: 0,
                                        }}
                                        style={{
                                            overflow: 'clip',
                                        }}
                                    />
                                )}
                            </AnimatePresence>
                        </Stack>

                        {/* If chat disabled need to add a black 32px seperator to align center. */}
                        {!orgConfig.disableChat ? (
                            <ToolbarItem props={chatButton} />
                        ) : (
                            <Box sx={{ width: '32px' }} />
                        )}
                    </Stack>

                    {orgConfig &&
                        !orgConfig.disableLocationServices &&
                        userLocation && (
                            <div
                                style={{
                                    padding: '16px',
                                }}
                            >
                                <SlideCardRow
                                    iconKey={'Location'}
                                    action={() => {
                                        closeOverlay()

                                        window.pushPath(Paths.home)
                                        homeTabSignal.value = 'map'

                                        const lngLatLabelledAppUser: any = {
                                            lngLat: new LngLat(
                                                userLocation.lngLat?.lng ?? 0,
                                                userLocation.lngLat?.lat ?? 0
                                            ),
                                            object: {
                                                ...selectedUser,
                                                timestamp:
                                                    userLocation.timestamp,
                                            },
                                        }

                                        selectLngLatLabelledTeamMember(
                                            lngLatLabelledAppUser,
                                            lngLatLabelledAppUser.lngLat
                                        )
                                    }}
                                    iconBackgroundColor={
                                        ColoursObj.core.secondary
                                    }
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'left',
                                        }}
                                    >
                                        <AddressLabel
                                            lngLat={
                                                new LngLat(
                                                    userLocation.lngLat?.lng ??
                                                        0,
                                                    userLocation.lngLat?.lat ??
                                                        0
                                                )
                                            }
                                            typographyProps={{
                                                color: getContrastYIQ(
                                                    ColoursObj.backgrounds
                                                        .header
                                                ),
                                            }}
                                        />
                                        <Typography
                                            color={getContrastYIQ(
                                                ColoursObj.backgrounds.header
                                            )}
                                            style={{
                                                opacity: 0.8,
                                            }}
                                            variant="bodySubtext"
                                        >
                                            {Math.abs(
                                                (userLocation?.timestamp ?? 0) -
                                                    Date.now()
                                            ) <
                                            1000 * 10
                                                ? 'Live'
                                                : new EventrDate(
                                                      userLocation.timestamp
                                                  )?.userLocalLuxon.toRelative()}
                                        </Typography>
                                    </div>
                                </SlideCardRow>
                            </div>
                        )}

                    <Tabs
                        value={selectedTabIndex}
                        onChange={(event, newValue) =>
                            setSelectedTabIndex(newValue)
                        }
                        centered={tabs.length === 1}
                        indicatorColor="secondary"
                        TabIndicatorProps={
                            tabs.length === 1
                                ? {
                                      style: {
                                          width: '100%',
                                          left: 0,
                                          right: 0,
                                      },
                                  }
                                : {}
                        }
                        style={{
                            boxShadow: headerCollapsed
                                ? '0px 4px 8px 0px rgba(51, 82, 111, 0.1)'
                                : undefined,
                            minHeight: '44px',
                            height: '44px',
                            color: ColoursObj.shades.shade5,
                        }}
                    >
                        {tabs.map((tab, index) => {
                            const selected = selectedTabIndex === index
                            const isSingleTab = tabs.length === 1
                            const tabWidth = isSingleTab
                                ? '100%'
                                : `${100 / tabs.length}%`
                            const justifyContent = isSingleTab
                                ? 'center'
                                : 'flex-start'

                            return (
                                <Tab
                                    key={index}
                                    disableRipple
                                    label={
                                        tab === 'Itinerary'
                                            ? i18n.t('home.itinerary')
                                            : i18n.t('account.profile.title')
                                    }
                                    value={index}
                                    style={{
                                        width: tabWidth,
                                        minHeight: '44px',
                                        height: '44px',
                                        textTransform: 'none',
                                        fontFamily: selected
                                            ? 'Inter-600-900'
                                            : 'Inter-100-500',
                                        fontWeight: selected ? 600 : 400,
                                        color: ColoursObj.shades.shade5,
                                        display: 'flex',
                                        justifyContent: justifyContent,
                                        alignItems: 'center',
                                    }}
                                />
                            )
                        })}
                    </Tabs>
                </SlideImage>
            </Box>

            <Box
                sx={{
                    overflowY: 'scroll',
                    overscrollBehavior: 'none',
                }}
                ref={containerRef}
            >
                {selectedTabIndex === 0 ? (
                    dataToDisplay(selectedItinerary?.v2 ?? []) ? (
                        <RendererSplitter
                            v2Data={selectedItinerary?.v2 ?? []}
                            remove="event_information"
                            screenType="teamMember"
                        />
                    ) : (
                        <Stack
                            alignItems={'center'}
                            justifyContent={'center'}
                            {...dismissHandlers}
                        >
                            <Typography
                                padding="64px 32px"
                                align="center"
                                variant="bodyRegular"
                                color={ColoursObj.shades.shade2}
                            >
                                {`${
                                    selectedUser?.firstName ?? 'This user'
                                } ${i18n.t(
                                    'team.user-has-no-itinerary-entries'
                                )}`}
                            </Typography>
                        </Stack>
                    )
                ) : (
                    <Stack gap="8px" padding="16px">
                        {selectedUser && <ProfileDetails user={selectedUser} />}
                    </Stack>
                )}
                <Box
                    height={'32px'}
                    sx={{ backgroundColor: ColoursObj.backgrounds.primary }}
                />
            </Box>
        </Box>
    )
}
